import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { UserSvgIcon, ReservationSvgIcon } from "../../icons/SvgIcons";
import Tooltip from "../ToolTipWrapper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPowerOff, faCircleDot } from "@fortawesome/free-solid-svg-icons";
import { iconStatusMap, statusGroups } from "./../DefaultImports";

const DEFAULT_STATUS_COLOR = "#6b8c88";

const ICON_COLOR_VARIATION_STORAGE_KEY = "reservation_icon_color_variation";
const ICON_COLOR_VARIATION_CHANGE_EVENT =
  "reservation_icon_color_variation_change";

const STATUS_COLORS = {
  "A CHEGAR": "#2F80ED",
  ATRASADA: "#D97706",
  "À PORTA": "#7C3AED",
  REST: "#0F766E",
  CLIENTE: "#2563EB",
  ESPERA: "#B45309",
  FINALIZADA: "#64748B",
  "Mesa livre": "#16A34A",
  CANCELADAS: "#DC2626",
  TOTAL: "#334155",
};

const getStoredIconColorVariation = () => {
  if (typeof window === "undefined") return 2;

  const storedValue = window.localStorage.getItem(
    ICON_COLOR_VARIATION_STORAGE_KEY,
  );

  if (storedValue === "1") return 1;
  if (storedValue === "2") return 2;

  return 2;
};

const getVariationColor = (iconData, selectedColorVariation, label) => {
  const variationColor =
    iconData?.[`color_variation_${selectedColorVariation}`]?.trim?.();

  if (variationColor) return variationColor;

  const normalColor = iconData?.color?.trim?.();

  if (normalColor) return normalColor;

  return STATUS_COLORS[label] || DEFAULT_STATUS_COLOR;
};

const TableWrapper = styled.div`
  background-color: #fff;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: stretch;
  margin: 0;
  padding: 0;
  overflow: hidden;
  width: 100%;
  min-width: 0;
  border: 1px solid #edf0ef;
`;

const StyledTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.68rem;
  border-spacing: 0;
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    font-size: 0.64rem;
  }
`;

const Th = styled.th`
  border: 1px solid #d0dad9;
  color: #2e3e3b;
  background-color: #fff;
  text-align: center;
  font-weight: 600;
  white-space: nowrap;
  font-size: 10px;
  vertical-align: middle;
  padding: 4px 1px;
  width: ${({ $small }) => ($small ? "28px" : "auto")};
`;

const Td = styled.td`
  border: 1px solid #d0dad9;
  text-align: center;
  height: 22px;
  line-height: 1;
  padding: 1px 2px;
  font-size: 0.68rem;
  font-weight: 700;
  font-family: "Roboto", sans-serif;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  background-color: ${({ isSelected }) => (isSelected ? "#b6ccc8" : "#ffffff")};
  color: ${({ isSelected }) => (isSelected ? "#ffffff" : "#6b8c88")};
  transition:
    background-color 0.15s ease,
    transform 0.1s ease;

  &:hover {
    background-color: ${({ isSelected, $clickable }) =>
      !$clickable ? "#ffffff" : isSelected ? "#a1bab6" : "#eef2f1"};
    transform: ${({ $clickable }) => ($clickable ? "scale(1.02)" : "none")};
  }
`;

const RowLabel = styled.td`
  border: 1px solid #d0dad9;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  padding: 0;
  height: 22px;
  width: 28px;
  min-width: 28px;
  vertical-align: middle;
`;

const IconSpan = styled.span`
  color: ${({ $color }) => $color || DEFAULT_STATUS_COLOR};
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    max-width: 17px;
    max-height: 17px;
  }
`;

const BottomRightTd = styled(Td)`
  border-bottom-right-radius: 0px;
`;

function SelectableStatusOptions({
  onSelectChange,
  selectedItems = [],
  reservations = [],
}) {
  const [statusSummary, setStatusSummary] = useState({});
  const [selectedColorVariation, setSelectedColorVariation] = useState(
    getStoredIconColorVariation,
  );

  const allDisplayLabels = [
    "A CHEGAR",
    "À PORTA",
    "ATRASADA",
    "REST",
    "CLIENTE",
    "ESPERA",
    "FINALIZADA",
    "Mesa livre",
    "CANCELADAS",
  ].filter((label) => statusGroups[label]);

  const selectableLabels = allDisplayLabels.filter(
    (label) => label !== "CANCELADAS",
  );

  useEffect(() => {
    const updateColorVariation = (event) => {
      if (
        event?.type === "storage" &&
        event.key !== ICON_COLOR_VARIATION_STORAGE_KEY
      ) {
        return;
      }

      if (
        event?.type === ICON_COLOR_VARIATION_CHANGE_EVENT &&
        (event.detail === 1 || event.detail === 2)
      ) {
        setSelectedColorVariation(event.detail);
        return;
      }

      setSelectedColorVariation(getStoredIconColorVariation());
    };

    window.addEventListener("storage", updateColorVariation);
    window.addEventListener(
      ICON_COLOR_VARIATION_CHANGE_EVENT,
      updateColorVariation,
    );

    return () => {
      window.removeEventListener("storage", updateColorVariation);
      window.removeEventListener(
        ICON_COLOR_VARIATION_CHANGE_EVENT,
        updateColorVariation,
      );
    };
  }, []);

  useEffect(() => {
    const summary = {};

    allDisplayLabels.forEach((label) => {
      summary[label] = { people: 0, reservations: 0 };
    });

    if (Array.isArray(reservations)) {
      reservations.forEach((res) => {
        for (const [label, statuses] of Object.entries(statusGroups)) {
          if (summary[label] && statuses.includes(res.status)) {
            summary[label].people += res.number_of_people || 0;
            summary[label].reservations += 1;
          }
        }
      });
    }

    setStatusSummary(summary);
  }, [reservations, allDisplayLabels.join("|")]);

  const areAllSelected = selectableLabels.every((label) =>
    selectedItems.includes(label),
  );

  const toggleSelection = (label) => {
    if (selectedItems.includes(label)) {
      onSelectChange(selectedItems.filter((l) => l !== label));
    } else {
      onSelectChange([...selectedItems, label]);
    }
  };

  const toggleAll = () => {
    if (areAllSelected) {
      onSelectChange([]);
    } else {
      onSelectChange([...selectableLabels]);
    }
  };

  const calculateSelectedTotal = (field) => {
    const labelsToSum = selectableLabels.filter((label) =>
      selectedItems.includes(label),
    );

    return labelsToSum.reduce(
      (acc, label) => acc + (statusSummary[label]?.[field] || 0),
      0,
    );
  };

  const getIconData = (label) => {
    const iconData = iconStatusMap[label] || {
      icon: faCircleDot,
      label,
    };

    return {
      ...iconData,
      color: getVariationColor(iconData, selectedColorVariation, label),
    };
  };

  return (
    <TableWrapper>
      <StyledTable>
        <thead>
          <tr>
            <Th $small>
              <Tooltip
                text={
                  areAllSelected ? "Desselecionar todos" : "Selecionar todos"
                }
              >
                <IconSpan
                  onClick={toggleAll}
                  $color="#99b0ad"
                  style={{ cursor: "pointer" }}
                >
                  <FontAwesomeIcon icon={faPowerOff} />
                </IconSpan>
              </Tooltip>
            </Th>

            {selectableLabels.map((label) => {
              const iconData = getIconData(label);

              return (
                <Th key={label}>
                  <Tooltip text={iconData.label}>
                    <IconSpan $color={iconData.color}>
                      <FontAwesomeIcon icon={iconData.icon} />
                    </IconSpan>
                  </Tooltip>
                </Th>
              );
            })}

            <Th>
              <Tooltip text={getIconData("TOTAL").label}>
                <IconSpan $color={getIconData("TOTAL").color}>
                  <FontAwesomeIcon icon={getIconData("TOTAL").icon} />
                </IconSpan>
              </Tooltip>
            </Th>

            {statusGroups["CANCELADAS"] && (
              <Th>
                <Tooltip text={getIconData("CANCELADAS").label}>
                  <IconSpan $color={getIconData("CANCELADAS").color}>
                    <FontAwesomeIcon icon={getIconData("CANCELADAS").icon} />
                  </IconSpan>
                </Tooltip>
              </Th>
            )}
          </tr>
        </thead>

        <tbody>
          <tr>
            <RowLabel>
              <Tooltip text="Nº de clientes">
                <UserSvgIcon
                  width={12}
                  height={12}
                  strokeColor="#2e3e3b"
                  style={{ minWidth: "12px" }}
                />
              </Tooltip>
            </RowLabel>

            {selectableLabels.map((label) => (
              <Td
                key={`people-${label}`}
                isSelected={selectedItems.includes(label)}
                $clickable
                onClick={() => toggleSelection(label)}
              >
                {statusSummary[label]?.people || 0}
              </Td>
            ))}

            <Td isSelected={false} $clickable={false}>
              {calculateSelectedTotal("people")}
            </Td>

            {statusGroups["CANCELADAS"] && (
              <Td
                isSelected={selectedItems.includes("CANCELADAS")}
                $clickable
                onClick={() => toggleSelection("CANCELADAS")}
              >
                {statusSummary["CANCELADAS"]?.people || 0}
              </Td>
            )}
          </tr>

          <tr>
            <RowLabel>
              <Tooltip text="Nº de reservas">
                <ReservationSvgIcon
                  width={12}
                  height={12}
                  strokeColor="#2e3e3b"
                  style={{ minWidth: "12px" }}
                />
              </Tooltip>
            </RowLabel>

            {selectableLabels.map((label) => (
              <Td
                key={`res-${label}`}
                isSelected={selectedItems.includes(label)}
                $clickable
                onClick={() => toggleSelection(label)}
              >
                {statusSummary[label]?.reservations || 0}
              </Td>
            ))}

            <Td isSelected={false} $clickable={false}>
              {calculateSelectedTotal("reservations")}
            </Td>

            {statusGroups["CANCELADAS"] && (
              <BottomRightTd
                isSelected={selectedItems.includes("CANCELADAS")}
                $clickable
                onClick={() => toggleSelection("CANCELADAS")}
              >
                {statusSummary["CANCELADAS"]?.reservations || 0}
              </BottomRightTd>
            )}
          </tr>
        </tbody>
      </StyledTable>
    </TableWrapper>
  );
}

export default SelectableStatusOptions;
